<!-- eslint-disable vue/no-v-html -->
<template>
  <BaseCard
    :result="{
      orgName: searchResult.secondary_organization.name,
      orgLink: searchResult.link,
      tagName: $t(`search.events.tagName`),
      tagLink: `${searchResult.link}/events`,
      id: searchResult.id,
      type: searchResult.type,
      result_position: resultPosition,
      display_text: searchResult.title,
      category: 'search_result',
    }"
    class="event-card result-card"
    @result-clicked="handleResultClick"
  >
    <template #preview>
      <div
        class="preview-fallback"
        data-testid="event-card-preview-fallback"
      >
        <div
          class="day"
          data-testid="event-card-preview-day"
        >
          {{ shortDay }}
        </div>
        <div
          class="month"
          data-testid="event-card-preview-month"
        >
          {{ shortMonth }}
        </div>
      </div>
    </template>

    <template #result-content>
      <a
        :href="url"
        class="title"
        data-testid="event-card-title"
        @click.stop="handleResultClick"
        >{{ searchResult.title }}</a
      >
      <div
        v-if="searchResult.description"
        class="snippet"
        data-testid="event-card-description"
      >
        <span v-html="searchResult.description"></span>
      </div>

      <div
        class="meta-info"
        :class="{ 'is-mobile-view': isMobileView }"
      >
        <div
          class="date"
          data-testid="event-card-date"
        >
          <Icon
            name="lucide:calendar"
            size="14"
          />
          {{ formattedEventDateAndTime }}
        </div>
        <a
          v-if="searchResult.venue"
          :href="`http://maps.google.com/?q=${encodeURIComponent(searchResult.venue)}`"
          class="location"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="event-card-venue"
        >
          <Icon
            name="lucide:map-pin"
            size="14"
          />
          {{ searchResult.venue }}
        </a>
      </div>

      <div class="card-buttons">
        <button
          ref="add-to-calendar-button-events"
          class="button add-to-calendar"
          :aria-expanded="showCalendarButtons.toString()"
          :aria-controls="`add-buttons-${searchResult.id}`"
          :aria-label="$t('search.events.addToCalendarAriaLabel', { eventTitle: searchResult.title })"
          data-testid="event-card-add-to-calendar-button"
          @click.stop="toggleCalendarButtons"
          @keydown.stop="accessibility.handleTriggerKeydown($event, showCalendarButtons, toggleCalendarButtons)"
        >
          <Icon
            name="lucide:calendar-plus"
            size="14"
          />
          <span>{{ $t('search.events.addToCalendar') }}</span>
        </button>
        <!-- <button class="button register">
            <Icon name="lucide:ticket" size="14" />
            {{ $t('search.events.register') }}
          </button> -->
        <div
          v-show="showCalendarButtons"
          :id="`add-buttons-${searchResult.id}`"
          class="calendar-popup"
          :class="{ 'is-visible': showCalendarButtons }"
          data-testid="event-card-calendar-popup"
        >
          <EventListButtons
            :calendar-options="calendarOptions"
            :show-calendar-buttons="showCalendarButtons"
            :event-data="searchResult"
            :handle-back-button-click="handleBackButtonClick"
            :handle-blur="handleBlur"
            :color-overrides="colorOverrides"
            use-new-close-icon
          />
        </div>
        <PublishedUpdatedDate :search-result="searchResult" />
      </div>
    </template>
  </BaseCard>
</template>

<script setup>
import BaseCard from './BaseCard.vue';
import { computed, ref, useTemplateRef, nextTick } from 'vue';
import { useScreenBreakpoints } from '@/composables/useBreakpoints';
import { useThemedButtons } from '@/composables/useThemedButtons';
import { useAccessibleDropdown } from '@/composables/useAccessibleDropdown';
import EventListButtons from '@/components/events/components/EventViews/List/EventListButtons.vue';
import PublishedUpdatedDate from './PublishedUpdatedDate.vue';
import { useCardShortFormattedDate } from '~/composables/useCardShortFormattedDate';

const accessibility = useAccessibleDropdown({
  onClose: () => {},
  scrollBehavior: 'smooth',
  typeAheadTimeout: 500,
  wrapNavigation: true,
});

const addToCalendarButton = useTemplateRef('add-to-calendar-button-events');

const { isMobileView } = useScreenBreakpoints();

const props = defineProps({
  searchResult: {
    type: Object,
    required: true,
  },
  resultPosition: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['result-clicked']);

const calendarOptions = [
  { idx: 0, name: 'Google', value: 'google' },
  { idx: 1, name: 'Outlook', value: 'outlook' },
  { idx: 2, name: 'iCal', value: 'iCal' },
];

const formattedEventDateAndTime = computed(() => {
  const formattedDate = props.searchResult.formatted_date;
  const lastFiveCharactersInDate = formattedDate.slice(formattedDate.length - 5, formattedDate.length);
  const allDayEventsWithNoTime = lastFiveCharactersInDate === ' from';
  if (allDayEventsWithNoTime) {
    return formattedDate.slice(0, -5);
  }
  return formattedDate;
});

const { month: shortMonth, day: shortDay } = useCardShortFormattedDate(props.searchResult.formatted_date);

const url = computed(() => {
  return `${props.searchResult.link}/events?=${props.searchResult.id}`;
});

async function handleResultClick() {
  await navigateTo(url.value, { external: true });
  emit('result-clicked', {
    position: props.resultPosition,
    url: url.value,
  });
}

const showCalendarButtons = ref(false);

function toggleCalendarButtons() {
  showCalendarButtons.value = !showCalendarButtons.value;
  if (showCalendarButtons.value) {
    const calendarOption = useTemplateRef('calendar-option-0');

    nextTick(() => {
      calendarOption[0]?.focus();
    });
  }
}
function handleBackButtonClick() {
  showCalendarButtons.value = false;
  addToCalendarButton.value.focus();
}
function handleBlur() {
  showCalendarButtons.value = false;
  addToCalendarButton.value.focus();
}
const { themedButtons } = useThemedButtons();
const colorOverrides = computed(() => {
  return {
    '--button-border': themedButtons['--tertiary-button-border'],
    '--button-primary-bg': themedButtons['--primary-button-background'],
    '--button-primary-bg-hover': themedButtons['--secondary-button-active'],
    // focus state
    '--button-outline': themedButtons['--primary-button-focus-outline'],
    // close calendar button
    '--button-default-bg': themedButtons['--secondary-button-background'],
    '--button-default-text': themedButtons['--secondary-button-text'],
    '--button-primary-text': themedButtons['--primary-button-text-contrast'],
  };
});
</script>

<style scoped>
.event-card {
  --color-grey-50: #f7f7f7;
  --color-grey-700: #616161;
  --color-grey-950: #2d2d2d;
  --background-color: var(--color-grey-50);
  --text-color: var(--color-grey-950);

  .title {
    line-height: 120%;
  }

  .snippet {
    line-height: 19px;
  }

  .day {
    font-size: 1.5rem;
    font-weight: bold;
  }

  .month {
    font-size: 1rem;
    color: var(--color-grey-700);
    text-transform: uppercase;
  }
  .meta-info {
    &.is-mobile-view {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .iconify {
      flex-shrink: 0;
    }
  }
  .card-buttons {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    .button {
      padding: 0.5rem 1rem;
      cursor: pointer;
      font-weight: 500;
      transition:
        background 0.2s,
        color 0.2s;
      &.add-to-calendar {
        background-color: var(--secondary-button-background);
        color: var(--secondary-button-text);
        border: var(--secondary-button-border);
        border-radius: var(--secondary-button-border-radius);
        display: flex;
        align-items: center;
        margin-right: 12px;
        .iconify {
          margin-right: 6px;
        }
        &:hover {
          background-color: var(--secondary-button-hover);
        }
        &:focus-visible {
          outline: var(--secondary-button-focus-outline);
          outline-offset: 1px;
          border: var(--secondary-button-hover-border);
        }
      }
    }
    .calendar-popup {
      position: relative;
      z-index: 10;
      padding: 12px 0 8px 0;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      .button-container.open {
        background: var(--primary-white, #fff);
        position: absolute;
        left: -182px;
        margin-top: 24px;
        min-width: 180px;
        border: 1px solid var(--color-grey-100, #e0e0e0);
        border-radius: 12px;
        z-index: 100;
      }
    }
    /*.calendar-popup.is-visible {
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0);
      animation: fadeInPopup 0.18s;
    }
    .calendar-popup:focus-visible {
      box-shadow:
        0 0 0 2px var(--color-purple),
        0 2px 8px rgba(0, 0, 0, 0.08);
    }
    @keyframes fadeInPopup {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }*/
  }
}
</style>
